import { defineMessages } from '@/i18n'

/** Crews (kolone): list and detail. Identity edits overlay AU; attendance stays derived. */
export const crewsMessages = defineMessages({
  en: {
    title: 'Crews',
    subtitle: 'Crews, leaders and members as AU records them.',
    readOnlyNote: 'Edits stay in this app. AU is not changed.',
    editCrew: 'Edit crew',
    editNote: 'These corrections stay in this app. AU is not changed.',
    colHeadcount: 'In / roster',
    colCode: 'Code',
    identity: 'Identity',
    attendance: 'Attendance',
    couldNotSave: 'Could not save.',
    saving: 'Saving…',
    searchPlaceholder: 'crew, code, leader, Bauleiter, company…',
    searchLabel: 'Search crews',
    activeOnly: 'Active only',
    window: 'Window',
    lastDays: 'Last {days} days',
    statActive: 'Active crews',
    statPeople: 'People checked in',
    statAttention: 'Need a look',
    colCrew: 'Crew',
    colCompany: 'Company',
    colLeader: 'Leader',
    colBauleiter: 'Bauleiter',
    colMembers: 'Members',
    colRoster: 'Roster',
    colLastCheckIn: 'Last check-in',
    colChecks: 'Checks',
    active: 'Active',
    inactive: 'Inactive',
    warnLeaderNotCheckedIn: 'Leader not checked in',
    warnNotOnRoster: { one: '{count} not on roster', other: '{count} not on roster' },
    warnRosterLeft: { one: '{count} on roster has left', other: '{count} on roster have left' },
    warnBauleiterOutdated: "AU's crew record still shows the previous Bauleiter",
    shownOf: '{shown} of {total} crews',
    noCrews: 'No crews match.',
    loadingCrews: 'Loading crews',
    loadingCrew: 'Loading crew',
    notFound: 'Crew not found.',
    back: 'All crews',
    sectionMembers: 'Members — last {days} days',
    membersHint: 'Everyone checked in with this crew in AU — the crew as it really works. Days count work days only, not absences.',
    noMembers: 'No one was checked in during the last {days} days.',
    colName: 'Name',
    colDays: 'Days',
    colLastDay: 'Last day',
    colHired: 'Hired',
    badgeLeader: 'Leader',
    badgeNew: 'New',
    badgeNotOnRoster: 'Not on roster',
    badgeLeft: 'Left (HR)',
    sectionRoster: 'Standard crew in AU',
    rosterHint: "AU's roster. Kept by hand and often out of date — compare it with who is really checked in.",
    noRoster: 'No standard crew in AU.',
    colAdded: 'On roster since',
    colStatus: 'Status',
    employed: 'Employed',
    leftOn: 'Left {date}',
    left: 'Left',
    checkedIn: 'Checked in',
    notCheckedIn: 'Not checked in',
    sectionLeaderHistory: 'Leader history',
    sectionBauleiterChanges: 'Bauleiter changes',
    current: 'current',
    previously: 'previously {name}',
    noHistory: 'No history recorded.',
    lastCheckIn: 'Last check-in {date}',
    neverCheckedIn: 'No check-ins',
    sectionProjects: { one: 'Projects — {count} project', other: 'Projects — {count} projects' },
    projectsHint: 'Every project this crew has Aufmaß sessions on, newest first. Open one to see its routes on the RTK map.',
    noProjects: 'No Aufmaß sessions for this crew.',
    colProject: 'Project',
    colLeaders: 'Leader on site',
    colCrewDays: 'Crew-days',
    colMetres: 'Metres',
    colPeriod: 'Worked',
    openOnMap: 'Open on the map',
    projectDetails: 'Project details',
    rtkCount: 'RTK · {count}',
    reportedOnly: 'Reported only',
    reportedOnlyHint: 'Only reported Aufmaß quantities — no RTK measurements were recorded, so there is nothing to show on the map.',
    rtkByOthers: 'RTK recorded by other crews',
    tabOverview: 'Overview',
    tabPerformance: 'Performance',
    loadingPerformance: 'Loading performance',
    perfWindow: 'Last {days} days · report days only (a filed Aufmaß report, no data flag)',
    statMetresPerDay: 'Metres per report day',
    vsCompany: 'vs company',
    statMetres: 'Metres',
    statReportDays: 'Report days',
    statMetresPerHour: 'Metres per man-hour',
    trendTitle: 'Metres per report day, week by week',
    trendSubtitle: 'Last 26 weeks, against the company average for the same week.',
    trendThisCrew: 'This crew',
    trendCompany: 'Company average',
    qualityTitle: 'Quality',
    qualityRtkFix: 'RTK fixed solution',
    qualityOfSegments: '{fix} of {total} measurements (all time)',
    qualityAccuracy: 'Mean accuracy, fixed',
    qualityNoRtk: 'This crew has recorded no RTK measurements — its work exists only as reported quantities.',
    qualityRepairs: 'Repair days',
    qualityFlagged: 'Flagged days',
    qualityFlaggedHint: 'Impossible quantity or a shift never closed — needs a human.',
    qualityNoReport: 'Days without a report',
    qualityNoReportHint: 'Checked in, but no Aufmaß report filed.',
    rankTitle: 'Ranking',
    rankHint: 'Among {count} crews with at least {min} report days in the last {days} days. 1 = best.',
    rankMetresPerDay: 'Metres per day',
    rankMetres: 'Total metres',
    rankDays: 'Days worked',
    rankFix: 'RTK fix rate',
    rankOf: 'of {count}',
    notRanked: 'Not ranked',
    buildTitle: 'What they build',
    buildSubtitle: 'Quantity by Aufmaß position, last {days} days',
    noReportsInWindow: 'No filed Aufmaß reports in the last {days} days.',
    tabMap: 'Map & photos',
    loadingMap: 'Loading map',
    mapTitle: 'Where they worked (RTK)',
    mapSummary: { one: '{n} measurements · {metres} m · {count} project', other: '{n} measurements · {metres} m · {count} projects' },
    mapNoRtk: 'No RTK measurements recorded by this crew — its work exists only as reported Aufmaß quantities, so there is nothing to draw. The photos below come from its reports.',
    mapReplay: 'Replay day by day',
    mapPause: 'Pause',
    mapTimeline: 'Day',
    photosTitle: 'Latest photos',
    photosHint: 'From the crew\'s Aufmaß reports and RTK measurements, newest first.',
    noPhotos: 'No photos yet.',
    photoReport: 'Report',
    closePhoto: 'Close photo',
    tabAttendance: 'Attendance',
    loadingAttendance: 'Loading attendance',
    calendarTitle: 'Days worked — last 26 weeks',
    calendarSummary: '{worked} days worked · {off} days booked as not working · {gaps} weekdays with no entry',
    calHover: 'Hover a day for details.',
    calWorked: { one: '{count} person worked · {metres} m reported', other: '{count} people worked · {metres} m reported' },
    calNotWorking: { one: '{count} person booked as not working', other: '{count} people booked as not working' },
    calNothing: 'No entry',
    calLegendWorked: 'Worked (darker = more metres)',
    calLegendOff: 'Not working',
    calLegendNone: 'No entry',
    stabilityTitle: 'Crew stability',
    stabilityHint: 'People who worked in the crew each month; +came in, −dropped out vs the month before.',
    monthTooltip: '{members} people · +{inn} / −{out} · {days} worker-days',
    tenureTitle: 'In this crew since',
    tenureSince: 'since {date}',
    tenureDays: { one: '{count} day', other: '{count} days' },
    disciplineTitle: 'Check-in records',
    disciplineHint: 'Last 90 days. AU stores no real clock-in times, so this is about the record: entered on the day, closed, and by whom.',
    discSameDay: 'Entered the same day',
    discMorning: 'Entered before 10:00',
    discLater: 'Entered days later',
    discUnclosed: 'Shifts never closed',
    discUnclosedHint: 'Checked in, never checked out: the hours for that day are unknown.',
    discLeaderMissing: 'Days without the leader',
    discEntries: 'Entries',
    recordedByTitle: 'Entered by',
    tabBonus: 'Bonus',
    loadingBonus: 'Loading bonus',
    bonusIntro: 'A preview under the rules set on the Bonus page (norm {norm} per hour) — the same formula, for this crew. Nothing here is paid or saved.',
    bonusOpenPage: 'Open the Bonus page →',
    bonusMonth: 'Month',
    bonusHours: 'Man-hours',
    bonusEarnings: 'Production earnings',
    bonusPerHour: '€ per hour',
    bonusNorm: 'Norm fulfilled',
    bonusExtras: 'Meal & bonuses',
    bonusExtrasHint: 'Meal {meal} · leader {leader} · {newWorkers} new workers {newBonus}',
    bonusTotal: 'Total',
    bonusVsMonthBefore: 'vs month before',
    bonusNormBar: 'Norm fulfilled ({norm} per hour = 100%)',
    bonusChartTitle: 'Total by month',
    bonusChartSubtitle: 'Last 6 months; the current month is still running.',
    bonusAssumptions: 'Earnings = Σ position quantity × bonus rate, over crew-days with hours. A new worker = hired within {window} days before the month ends; the new-worker amount is assumed to be paid once per new worker per month.',
  },
  de: {
    title: 'Kolonnen',
    subtitle: 'Kolonnen, Kolonnenführer und Mitglieder laut AU.',
    readOnlyNote: 'Änderungen bleiben in dieser App. AU wird nicht geändert.',
    editCrew: 'Kolonne bearbeiten',
    editNote: 'Diese Korrekturen bleiben in dieser App. AU wird nicht geändert.',
    colHeadcount: 'An / Stamm',
    colCode: 'Code',
    identity: 'Identität',
    attendance: 'Anwesenheit',
    couldNotSave: 'Speichern fehlgeschlagen.',
    saving: 'Speichern…',
    searchPlaceholder: 'Kolonne, Code, Führer, Bauleiter, Firma…',
    searchLabel: 'Kolonnen durchsuchen',
    activeOnly: 'Nur aktive',
    window: 'Zeitraum',
    lastDays: 'Letzte {days} Tage',
    statActive: 'Aktive Kolonnen',
    statPeople: 'Angemeldete Personen',
    statAttention: 'Zu prüfen',
    colCrew: 'Kolonne',
    colCompany: 'Firma',
    colLeader: 'Kolonnenführer',
    colBauleiter: 'Bauleiter',
    colMembers: 'Mitglieder',
    colRoster: 'Stammkolonne',
    colLastCheckIn: 'Letzte Anmeldung',
    colChecks: 'Hinweise',
    active: 'Aktiv',
    inactive: 'Inaktiv',
    warnLeaderNotCheckedIn: 'Führer nicht angemeldet',
    warnNotOnRoster: { one: '{count} nicht in Stammkolonne', other: '{count} nicht in Stammkolonne' },
    warnRosterLeft: { one: '{count} aus Stammkolonne ausgeschieden', other: '{count} aus Stammkolonne ausgeschieden' },
    warnBauleiterOutdated: 'Der AU-Kolonnendatensatz zeigt noch den vorherigen Bauleiter',
    shownOf: '{shown} von {total} Kolonnen',
    noCrews: 'Keine passenden Kolonnen.',
    loadingCrews: 'Kolonnen werden geladen',
    loadingCrew: 'Kolonne wird geladen',
    notFound: 'Kolonne nicht gefunden.',
    back: 'Alle Kolonnen',
    sectionMembers: 'Mitglieder — letzte {days} Tage',
    membersHint: 'Alle, die in AU mit dieser Kolonne angemeldet wurden — so arbeitet die Kolonne tatsächlich. Tage zählen nur Arbeitstage, keine Abwesenheiten.',
    noMembers: 'In den letzten {days} Tagen wurde niemand angemeldet.',
    colName: 'Name',
    colDays: 'Tage',
    colLastDay: 'Letzter Tag',
    colHired: 'Eingestellt',
    badgeLeader: 'Führer',
    badgeNew: 'Neu',
    badgeNotOnRoster: 'Nicht in Stammkolonne',
    badgeLeft: 'Ausgeschieden (HR)',
    sectionRoster: 'Stammkolonne in AU',
    rosterHint: 'Die Stammliste in AU. Wird von Hand gepflegt und ist oft veraltet — mit den tatsächlichen Anmeldungen vergleichen.',
    noRoster: 'Keine Stammkolonne in AU.',
    colAdded: 'In Stammkolonne seit',
    colStatus: 'Status',
    employed: 'Beschäftigt',
    leftOn: 'Ausgeschieden {date}',
    left: 'Ausgeschieden',
    checkedIn: 'Angemeldet',
    notCheckedIn: 'Nicht angemeldet',
    sectionLeaderHistory: 'Verlauf Kolonnenführer',
    sectionBauleiterChanges: 'Bauleiter-Wechsel',
    current: 'aktuell',
    previously: 'vorher {name}',
    noHistory: 'Kein Verlauf erfasst.',
    lastCheckIn: 'Letzte Anmeldung {date}',
    neverCheckedIn: 'Keine Anmeldungen',
    sectionProjects: { one: 'Projekte — {count} Projekt', other: 'Projekte — {count} Projekte' },
    projectsHint: 'Alle Projekte mit Aufmaß-Einträgen dieser Kolonne, neueste zuerst. Öffnen zeigt die Trassen auf der RTK-Karte.',
    noProjects: 'Keine Aufmaß-Einträge für diese Kolonne.',
    colProject: 'Projekt',
    colLeaders: 'Kolonnenführer vor Ort',
    colCrewDays: 'Kolonnentage',
    colMetres: 'Meter',
    colPeriod: 'Gearbeitet',
    openOnMap: 'Auf der Karte öffnen',
    projectDetails: 'Projektdetails',
    rtkCount: 'RTK · {count}',
    reportedOnly: 'Nur gemeldet',
    reportedOnlyHint: 'Nur gemeldete Aufmaß-Mengen — es wurden keine RTK-Messungen aufgezeichnet, daher gibt es auf der Karte nichts zu zeigen.',
    rtkByOthers: 'RTK von anderen Kolonnen aufgezeichnet',
    tabOverview: 'Übersicht',
    tabPerformance: 'Leistung',
    loadingPerformance: 'Leistung wird geladen',
    perfWindow: 'Letzte {days} Tage · nur Berichtstage (Aufmaß-Bericht vorhanden, keine Datenmarkierung)',
    statMetresPerDay: 'Meter pro Berichtstag',
    vsCompany: 'ggü. Firma',
    statMetres: 'Meter',
    statReportDays: 'Berichtstage',
    statMetresPerHour: 'Meter pro Arbeitsstunde',
    trendTitle: 'Meter pro Berichtstag, Woche für Woche',
    trendSubtitle: 'Letzte 26 Wochen, verglichen mit dem Firmendurchschnitt derselben Woche.',
    trendThisCrew: 'Diese Kolonne',
    trendCompany: 'Firmendurchschnitt',
    qualityTitle: 'Qualität',
    qualityRtkFix: 'RTK-Fix-Lösung',
    qualityOfSegments: '{fix} von {total} Messungen (gesamt)',
    qualityAccuracy: 'Mittlere Genauigkeit, Fix',
    qualityNoRtk: 'Diese Kolonne hat keine RTK-Messungen aufgezeichnet — ihre Arbeit liegt nur als gemeldete Mengen vor.',
    qualityRepairs: 'Reparaturtage',
    qualityFlagged: 'Markierte Tage',
    qualityFlaggedHint: 'Unmögliche Menge oder nie beendete Schicht — muss geprüft werden.',
    qualityNoReport: 'Tage ohne Bericht',
    qualityNoReportHint: 'Angemeldet, aber kein Aufmaß-Bericht eingereicht.',
    rankTitle: 'Rangliste',
    rankHint: 'Unter {count} Kolonnen mit mindestens {min} Berichtstagen in den letzten {days} Tagen. 1 = beste.',
    rankMetresPerDay: 'Meter pro Tag',
    rankMetres: 'Meter gesamt',
    rankDays: 'Arbeitstage',
    rankFix: 'RTK-Fix-Quote',
    rankOf: 'von {count}',
    notRanked: 'Nicht gewertet',
    buildTitle: 'Was sie bauen',
    buildSubtitle: 'Menge nach Aufmaß-Position, letzte {days} Tage',
    noReportsInWindow: 'Keine Aufmaß-Berichte in den letzten {days} Tagen.',
    tabMap: 'Karte & Fotos',
    loadingMap: 'Karte wird geladen',
    mapTitle: 'Wo sie gearbeitet haben (RTK)',
    mapSummary: { one: '{n} Messungen · {metres} m · {count} Projekt', other: '{n} Messungen · {metres} m · {count} Projekte' },
    mapNoRtk: 'Diese Kolonne hat keine RTK-Messungen aufgezeichnet — ihre Arbeit liegt nur als gemeldete Aufmaß-Mengen vor, daher gibt es nichts zu zeichnen. Die Fotos unten stammen aus ihren Berichten.',
    mapReplay: 'Tag für Tag abspielen',
    mapPause: 'Pause',
    mapTimeline: 'Tag',
    photosTitle: 'Neueste Fotos',
    photosHint: 'Aus den Aufmaß-Berichten und RTK-Messungen der Kolonne, neueste zuerst.',
    noPhotos: 'Noch keine Fotos.',
    photoReport: 'Bericht',
    closePhoto: 'Foto schließen',
    tabAttendance: 'Anwesenheit',
    loadingAttendance: 'Anwesenheit wird geladen',
    calendarTitle: 'Arbeitstage — letzte 26 Wochen',
    calendarSummary: '{worked} Arbeitstage · {off} Tage als nicht arbeitend gebucht · {gaps} Werktage ohne Eintrag',
    calHover: 'Für Details über einen Tag fahren.',
    calWorked: { one: '{count} Person hat gearbeitet · {metres} m gemeldet', other: '{count} Personen haben gearbeitet · {metres} m gemeldet' },
    calNotWorking: { one: '{count} Person als nicht arbeitend gebucht', other: '{count} Personen als nicht arbeitend gebucht' },
    calNothing: 'Kein Eintrag',
    calLegendWorked: 'Gearbeitet (dunkler = mehr Meter)',
    calLegendOff: 'Nicht gearbeitet',
    calLegendNone: 'Kein Eintrag',
    stabilityTitle: 'Stabilität der Kolonne',
    stabilityHint: 'Personen, die pro Monat in der Kolonne gearbeitet haben; +neu, −weg gegenüber dem Vormonat.',
    monthTooltip: '{members} Personen · +{inn} / −{out} · {days} Personentage',
    tenureTitle: 'In dieser Kolonne seit',
    tenureSince: 'seit {date}',
    tenureDays: { one: '{count} Tag', other: '{count} Tage' },
    disciplineTitle: 'Anmeldungen',
    disciplineHint: 'Letzte 90 Tage. AU speichert keine echten Anmeldezeiten — es geht um den Eintrag: am selben Tag, abgeschlossen, und von wem.',
    discSameDay: 'Am selben Tag eingetragen',
    discMorning: 'Vor 10:00 eingetragen',
    discLater: 'Tage später eingetragen',
    discUnclosed: 'Nie abgeschlossene Schichten',
    discUnclosedHint: 'Angemeldet, nie abgemeldet: die Stunden dieses Tages sind unbekannt.',
    discLeaderMissing: 'Tage ohne Kolonnenführer',
    discEntries: 'Einträge',
    recordedByTitle: 'Eingetragen von',
    tabBonus: 'Bonus',
    loadingBonus: 'Bonus wird geladen',
    bonusIntro: 'Eine Vorschau nach den Regeln der Bonus-Seite (Norm {norm} pro Stunde) — dieselbe Formel, für diese Kolonne. Hier wird nichts ausgezahlt oder gespeichert.',
    bonusOpenPage: 'Bonus-Seite öffnen →',
    bonusMonth: 'Monat',
    bonusHours: 'Arbeitsstunden',
    bonusEarnings: 'Produktionsverdienst',
    bonusPerHour: '€ pro Stunde',
    bonusNorm: 'Norm erfüllt',
    bonusExtras: 'Verpflegung & Boni',
    bonusExtrasHint: 'Verpflegung {meal} · Führer {leader} · {newWorkers} neue Arbeiter {newBonus}',
    bonusTotal: 'Gesamt',
    bonusVsMonthBefore: 'ggü. Vormonat',
    bonusNormBar: 'Norm erfüllt ({norm} pro Stunde = 100 %)',
    bonusChartTitle: 'Gesamt pro Monat',
    bonusChartSubtitle: 'Letzte 6 Monate; der laufende Monat ist noch nicht abgeschlossen.',
    bonusAssumptions: 'Verdienst = Σ Positionsmenge × Bonussatz, über Kolonnentage mit Stunden. Neuer Arbeiter = eingestellt innerhalb von {window} Tagen vor Monatsende; der Betrag für neue Arbeiter wird einmal pro neuem Arbeiter und Monat angenommen.',
  },
  cnr: {
    title: 'Kolone',
    subtitle: 'Kolone, vođe i članovi prema AU.',
    readOnlyNote: 'Izmjene ostaju u ovoj aplikaciji. AU se ne mijenja.',
    editCrew: 'Uredi kolonu',
    editNote: 'Ove ispravke ostaju u ovoj aplikaciji. AU se ne mijenja.',
    colHeadcount: 'U / sastav',
    colCode: 'Šifra',
    identity: 'Identitet',
    attendance: 'Prisustvo',
    couldNotSave: 'Nije sačuvano.',
    saving: 'Čuvanje…',
    searchPlaceholder: 'kolona, šifra, vođa, Bauleiter, firma…',
    searchLabel: 'Pretraga kolona',
    activeOnly: 'Samo aktivne',
    window: 'Period',
    lastDays: 'Posljednjih {days} dana',
    statActive: 'Aktivne kolone',
    statPeople: 'Prijavljenih osoba',
    statAttention: 'Za provjeru',
    colCrew: 'Kolona',
    colCompany: 'Firma',
    colLeader: 'Vođa kolone',
    colBauleiter: 'Bauleiter',
    colMembers: 'Članovi',
    colRoster: 'Standardni sastav',
    colLastCheckIn: 'Posljednja prijava',
    colChecks: 'Upozorenja',
    active: 'Aktivna',
    inactive: 'Neaktivna',
    warnLeaderNotCheckedIn: 'Vođa nije prijavljen',
    warnNotOnRoster: { one: '{count} nije u sastavu', few: '{count} nisu u sastavu', other: '{count} nije u sastavu' },
    warnRosterLeft: { one: '{count} iz sastava više ne radi', few: '{count} iz sastava više ne rade', other: '{count} iz sastava više ne radi' },
    warnBauleiterOutdated: 'AU zapis kolone još pokazuje prethodnog Bauleitera',
    shownOf: '{shown} od {total} kolona',
    noCrews: 'Nema kolona koje odgovaraju.',
    loadingCrews: 'Učitavanje kolona',
    loadingCrew: 'Učitavanje kolone',
    notFound: 'Kolona nije pronađena.',
    back: 'Sve kolone',
    sectionMembers: 'Članovi — posljednjih {days} dana',
    membersHint: 'Svi koji su u AU prijavljeni sa ovom kolonom — kolona kakva stvarno radi. Dani broje samo radne dane, ne odsustva.',
    noMembers: 'Niko nije prijavljen u posljednjih {days} dana.',
    colName: 'Ime',
    colDays: 'Dana',
    colLastDay: 'Posljednji dan',
    colHired: 'Zaposlen od',
    badgeLeader: 'Vođa',
    badgeNew: 'Nov',
    badgeNotOnRoster: 'Nije u sastavu',
    badgeLeft: 'Više ne radi (HR)',
    sectionRoster: 'Standardni sastav u AU',
    rosterHint: 'Spisak u AU. Vodi se ručno i često je zastario — uporedite ga sa stvarnim prijavama.',
    noRoster: 'Nema standardnog sastava u AU.',
    colAdded: 'U sastavu od',
    colStatus: 'Status',
    employed: 'Zaposlen',
    leftOn: 'Ne radi od {date}',
    left: 'Ne radi',
    checkedIn: 'Prijavljen',
    notCheckedIn: 'Nije prijavljen',
    sectionLeaderHistory: 'Istorija vođa',
    sectionBauleiterChanges: 'Promjene Bauleitera',
    current: 'trenutno',
    previously: 'prije: {name}',
    noHistory: 'Nema zabilježene istorije.',
    lastCheckIn: 'Posljednja prijava {date}',
    neverCheckedIn: 'Bez prijava',
    sectionProjects: { one: 'Projekti — {count} projekat', few: 'Projekti — {count} projekta', other: 'Projekti — {count} projekata' },
    projectsHint: 'Svi projekti na kojima ova kolona ima Aufmaß unose, najnoviji prvi. Otvorite projekat da vidite njegove trase na RTK mapi.',
    noProjects: 'Nema Aufmaß unosa za ovu kolonu.',
    colProject: 'Projekat',
    colLeaders: 'Vođa na terenu',
    colCrewDays: 'Dana kolone',
    colMetres: 'Metri',
    colPeriod: 'Rađeno',
    openOnMap: 'Otvori na mapi',
    projectDetails: 'Detalji projekta',
    rtkCount: 'RTK · {count}',
    reportedOnly: 'Samo prijavljeno',
    reportedOnlyHint: 'Postoje samo prijavljene Aufmaß količine — RTK mjerenja nisu snimljena, pa na mapi nema šta da se prikaže.',
    rtkByOthers: 'RTK snimile druge kolone',
    tabOverview: 'Pregled',
    tabPerformance: 'Učinak',
    loadingPerformance: 'Učitavanje učinka',
    perfWindow: 'Posljednjih {days} dana · samo dani s izvještajem (predat Aufmaß izvještaj, bez oznake greške)',
    statMetresPerDay: 'Metara po danu s izvještajem',
    vsCompany: 'u odnosu na firmu',
    statMetres: 'Metri',
    statReportDays: 'Dani s izvještajem',
    statMetresPerHour: 'Metara po radnom satu',
    trendTitle: 'Metara po danu s izvještajem, po nedjeljama',
    trendSubtitle: 'Posljednjih 26 nedjelja, u odnosu na prosjek firme za istu nedjelju.',
    trendThisCrew: 'Ova kolona',
    trendCompany: 'Prosjek firme',
    qualityTitle: 'Kvalitet',
    qualityRtkFix: 'RTK fiksno rješenje',
    qualityOfSegments: '{fix} od {total} mjerenja (ukupno)',
    qualityAccuracy: 'Prosječna tačnost, fiksno',
    qualityNoRtk: 'Ova kolona nema snimljenih RTK mjerenja — njen rad postoji samo kao prijavljene količine.',
    qualityRepairs: 'Dani popravki',
    qualityFlagged: 'Označeni dani',
    qualityFlaggedHint: 'Nemoguća količina ili smjena koja nije zatvorena — potrebna provjera.',
    qualityNoReport: 'Dani bez izvještaja',
    qualityNoReportHint: 'Prijavljeni, ali Aufmaß izvještaj nije predat.',
    rankTitle: 'Rang',
    rankHint: 'Među {count} kolona s najmanje {min} dana s izvještajem u posljednjih {days} dana. 1 = najbolja.',
    rankMetresPerDay: 'Metara po danu',
    rankMetres: 'Ukupno metara',
    rankDays: 'Radnih dana',
    rankFix: 'Udio RTK fiksa',
    rankOf: 'od {count}',
    notRanked: 'Nije rangirana',
    buildTitle: 'Šta grade',
    buildSubtitle: 'Količina po Aufmaß poziciji, posljednjih {days} dana',
    noReportsInWindow: 'Nema predatih Aufmaß izvještaja u posljednjih {days} dana.',
    tabMap: 'Mapa i fotografije',
    loadingMap: 'Učitavanje mape',
    mapTitle: 'Gdje su radili (RTK)',
    mapSummary: { one: '{n} mjerenja · {metres} m · {count} projekat', few: '{n} mjerenja · {metres} m · {count} projekta', other: '{n} mjerenja · {metres} m · {count} projekata' },
    mapNoRtk: 'Ova kolona nema snimljenih RTK mjerenja — njen rad postoji samo kao prijavljene Aufmaß količine, pa nema šta da se nacrta. Fotografije ispod su iz njenih izvještaja.',
    mapReplay: 'Reprodukuj dan po dan',
    mapPause: 'Pauza',
    mapTimeline: 'Dan',
    photosTitle: 'Najnovije fotografije',
    photosHint: 'Iz Aufmaß izvještaja i RTK mjerenja kolone, najnovije prve.',
    noPhotos: 'Još nema fotografija.',
    photoReport: 'Izvještaj',
    closePhoto: 'Zatvori fotografiju',
    tabAttendance: 'Prisustvo',
    loadingAttendance: 'Učitavanje prisustva',
    calendarTitle: 'Radni dani — posljednjih 26 nedjelja',
    calendarSummary: '{worked} radnih dana · {off} dana upisano kao neradno · {gaps} radnih dana bez unosa',
    calHover: 'Pređite preko dana za detalje.',
    calWorked: { one: '{count} osoba radila · prijavljeno {metres} m', few: '{count} osobe radile · prijavljeno {metres} m', other: '{count} osoba radilo · prijavljeno {metres} m' },
    calNotWorking: { one: '{count} osoba upisana kao neradna', few: '{count} osobe upisane kao neradne', other: '{count} osoba upisano kao neradno' },
    calNothing: 'Nema unosa',
    calLegendWorked: 'Radili (tamnije = više metara)',
    calLegendOff: 'Neradno',
    calLegendNone: 'Nema unosa',
    stabilityTitle: 'Stabilnost kolone',
    stabilityHint: 'Ljudi koji su radili u koloni po mjesecima; +došli, −otišli u odnosu na prethodni mjesec.',
    monthTooltip: '{members} ljudi · +{inn} / −{out} · {days} radnik-dana',
    tenureTitle: 'U ovoj koloni od',
    tenureSince: 'od {date}',
    tenureDays: { one: '{count} dan', few: '{count} dana', other: '{count} dana' },
    disciplineTitle: 'Evidencija prijava',
    disciplineHint: 'Posljednjih 90 dana. AU ne čuva stvarna vremena prijave, pa je riječ o samom unosu: istog dana, zatvoren, i ko ga je unio.',
    discSameDay: 'Uneseno istog dana',
    discMorning: 'Uneseno prije 10:00',
    discLater: 'Uneseno danima kasnije',
    discUnclosed: 'Nezatvorene smjene',
    discUnclosedHint: 'Prijavljen, nikad odjavljen: sati za taj dan nisu poznati.',
    discLeaderMissing: 'Dani bez vođe',
    discEntries: 'Unosi',
    recordedByTitle: 'Unio',
    tabBonus: 'Bonus',
    loadingBonus: 'Učitavanje bonusa',
    bonusIntro: 'Pregled po pravilima sa stranice Bonus (norma {norm} po satu) — ista formula, za ovu kolonu. Ništa se ovdje ne isplaćuje niti čuva.',
    bonusOpenPage: 'Otvori stranicu Bonus →',
    bonusMonth: 'Mjesec',
    bonusHours: 'Radni sati',
    bonusEarnings: 'Zarada od proizvodnje',
    bonusPerHour: '€ po satu',
    bonusNorm: 'Ispunjena norma',
    bonusExtras: 'Obrok i bonusi',
    bonusExtrasHint: 'Obrok {meal} · vođa {leader} · {newWorkers} novih radnika {newBonus}',
    bonusTotal: 'Ukupno',
    bonusVsMonthBefore: 'u odnosu na prethodni mjesec',
    bonusNormBar: 'Ispunjena norma ({norm} po satu = 100%)',
    bonusChartTitle: 'Ukupno po mjesecima',
    bonusChartSubtitle: 'Posljednjih 6 mjeseci; tekući mjesec još traje.',
    bonusAssumptions: 'Zarada = Σ količina pozicije × stopa bonusa, za dane kolone sa satima. Nov radnik = zaposlen unutar {window} dana prije kraja mjeseca; iznos za nove radnike računa se jednom po novom radniku mjesečno.',
  },
  bs: {
    title: 'Kolone',
    subtitle: 'Kolone, vođe i članovi prema AU.',
    readOnlyNote: 'Izmjene ostaju u ovoj aplikaciji. AU se ne mijenja.',
    editCrew: 'Uredi kolonu',
    editNote: 'Ove ispravke ostaju u ovoj aplikaciji. AU se ne mijenja.',
    colHeadcount: 'U / sastav',
    colCode: 'Šifra',
    identity: 'Identitet',
    attendance: 'Prisustvo',
    couldNotSave: 'Nije sačuvano.',
    saving: 'Čuvanje…',
    searchPlaceholder: 'kolona, šifra, vođa, Bauleiter, firma…',
    searchLabel: 'Pretraga kolona',
    activeOnly: 'Samo aktivne',
    window: 'Period',
    lastDays: 'Posljednjih {days} dana',
    statActive: 'Aktivne kolone',
    statPeople: 'Prijavljenih osoba',
    statAttention: 'Za provjeru',
    colCrew: 'Kolona',
    colCompany: 'Firma',
    colLeader: 'Vođa kolone',
    colBauleiter: 'Bauleiter',
    colMembers: 'Članovi',
    colRoster: 'Standardni sastav',
    colLastCheckIn: 'Posljednja prijava',
    colChecks: 'Upozorenja',
    active: 'Aktivna',
    inactive: 'Neaktivna',
    warnLeaderNotCheckedIn: 'Vođa nije prijavljen',
    warnNotOnRoster: { one: '{count} nije u sastavu', few: '{count} nisu u sastavu', other: '{count} nije u sastavu' },
    warnRosterLeft: { one: '{count} iz sastava više ne radi', few: '{count} iz sastava više ne rade', other: '{count} iz sastava više ne radi' },
    warnBauleiterOutdated: 'AU zapis kolone još pokazuje prethodnog Bauleitera',
    shownOf: '{shown} od {total} kolona',
    noCrews: 'Nema kolona koje odgovaraju.',
    loadingCrews: 'Učitavanje kolona',
    loadingCrew: 'Učitavanje kolone',
    notFound: 'Kolona nije pronađena.',
    back: 'Sve kolone',
    sectionMembers: 'Članovi — posljednjih {days} dana',
    membersHint: 'Svi koji su u AU prijavljeni s ovom kolonom — kolona kakva stvarno radi. Dani broje samo radne dane, ne odsustva.',
    noMembers: 'Niko nije prijavljen u posljednjih {days} dana.',
    colName: 'Ime',
    colDays: 'Dana',
    colLastDay: 'Posljednji dan',
    colHired: 'Zaposlen od',
    badgeLeader: 'Vođa',
    badgeNew: 'Nov',
    badgeNotOnRoster: 'Nije u sastavu',
    badgeLeft: 'Više ne radi (HR)',
    sectionRoster: 'Standardni sastav u AU',
    rosterHint: 'Spisak u AU. Vodi se ručno i često je zastario — uporedite ga sa stvarnim prijavama.',
    noRoster: 'Nema standardnog sastava u AU.',
    colAdded: 'U sastavu od',
    colStatus: 'Status',
    employed: 'Zaposlen',
    leftOn: 'Ne radi od {date}',
    left: 'Ne radi',
    checkedIn: 'Prijavljen',
    notCheckedIn: 'Nije prijavljen',
    sectionLeaderHistory: 'Historija vođa',
    sectionBauleiterChanges: 'Promjene Bauleitera',
    current: 'trenutno',
    previously: 'prije: {name}',
    noHistory: 'Nema zabilježene historije.',
    lastCheckIn: 'Posljednja prijava {date}',
    neverCheckedIn: 'Bez prijava',
    sectionProjects: { one: 'Projekti — {count} projekat', few: 'Projekti — {count} projekta', other: 'Projekti — {count} projekata' },
    projectsHint: 'Svi projekti na kojima ova kolona ima Aufmaß unose, najnoviji prvi. Otvorite projekat da vidite njegove trase na RTK mapi.',
    noProjects: 'Nema Aufmaß unosa za ovu kolonu.',
    colProject: 'Projekat',
    colLeaders: 'Vođa na terenu',
    colCrewDays: 'Dana kolone',
    colMetres: 'Metri',
    colPeriod: 'Rađeno',
    openOnMap: 'Otvori na mapi',
    projectDetails: 'Detalji projekta',
    rtkCount: 'RTK · {count}',
    reportedOnly: 'Samo prijavljeno',
    reportedOnlyHint: 'Postoje samo prijavljene Aufmaß količine — RTK mjerenja nisu snimljena, pa na mapi nema šta prikazati.',
    rtkByOthers: 'RTK snimile druge kolone',
    tabOverview: 'Pregled',
    tabPerformance: 'Učinak',
    loadingPerformance: 'Učitavanje učinka',
    perfWindow: 'Posljednjih {days} dana · samo dani s izvještajem (predat Aufmaß izvještaj, bez oznake greške)',
    statMetresPerDay: 'Metara po danu s izvještajem',
    vsCompany: 'u odnosu na firmu',
    statMetres: 'Metri',
    statReportDays: 'Dani s izvještajem',
    statMetresPerHour: 'Metara po radnom satu',
    trendTitle: 'Metara po danu s izvještajem, po sedmicama',
    trendSubtitle: 'Posljednjih 26 sedmica, u odnosu na prosjek firme za istu sedmicu.',
    trendThisCrew: 'Ova kolona',
    trendCompany: 'Prosjek firme',
    qualityTitle: 'Kvalitet',
    qualityRtkFix: 'RTK fiksno rješenje',
    qualityOfSegments: '{fix} od {total} mjerenja (ukupno)',
    qualityAccuracy: 'Prosječna tačnost, fiksno',
    qualityNoRtk: 'Ova kolona nema snimljenih RTK mjerenja — njen rad postoji samo kao prijavljene količine.',
    qualityRepairs: 'Dani popravki',
    qualityFlagged: 'Označeni dani',
    qualityFlaggedHint: 'Nemoguća količina ili smjena koja nije zatvorena — potrebna provjera.',
    qualityNoReport: 'Dani bez izvještaja',
    qualityNoReportHint: 'Prijavljeni, ali Aufmaß izvještaj nije predat.',
    rankTitle: 'Rang',
    rankHint: 'Među {count} kolona s najmanje {min} dana s izvještajem u posljednjih {days} dana. 1 = najbolja.',
    rankMetresPerDay: 'Metara po danu',
    rankMetres: 'Ukupno metara',
    rankDays: 'Radnih dana',
    rankFix: 'Udio RTK fiksa',
    rankOf: 'od {count}',
    notRanked: 'Nije rangirana',
    buildTitle: 'Šta grade',
    buildSubtitle: 'Količina po Aufmaß poziciji, posljednjih {days} dana',
    noReportsInWindow: 'Nema predatih Aufmaß izvještaja u posljednjih {days} dana.',
    tabMap: 'Mapa i fotografije',
    loadingMap: 'Učitavanje mape',
    mapTitle: 'Gdje su radili (RTK)',
    mapSummary: { one: '{n} mjerenja · {metres} m · {count} projekat', few: '{n} mjerenja · {metres} m · {count} projekta', other: '{n} mjerenja · {metres} m · {count} projekata' },
    mapNoRtk: 'Ova kolona nema snimljenih RTK mjerenja — njen rad postoji samo kao prijavljene Aufmaß količine, pa nema šta nacrtati. Fotografije ispod su iz njenih izvještaja.',
    mapReplay: 'Reprodukuj dan po dan',
    mapPause: 'Pauza',
    mapTimeline: 'Dan',
    photosTitle: 'Najnovije fotografije',
    photosHint: 'Iz Aufmaß izvještaja i RTK mjerenja kolone, najnovije prve.',
    noPhotos: 'Još nema fotografija.',
    photoReport: 'Izvještaj',
    closePhoto: 'Zatvori fotografiju',
    tabAttendance: 'Prisustvo',
    loadingAttendance: 'Učitavanje prisustva',
    calendarTitle: 'Radni dani — posljednjih 26 sedmica',
    calendarSummary: '{worked} radnih dana · {off} dana upisano kao neradno · {gaps} radnih dana bez unosa',
    calHover: 'Pređite preko dana za detalje.',
    calWorked: { one: '{count} osoba radila · prijavljeno {metres} m', few: '{count} osobe radile · prijavljeno {metres} m', other: '{count} osoba radilo · prijavljeno {metres} m' },
    calNotWorking: { one: '{count} osoba upisana kao neradna', few: '{count} osobe upisane kao neradne', other: '{count} osoba upisano kao neradno' },
    calNothing: 'Nema unosa',
    calLegendWorked: 'Radili (tamnije = više metara)',
    calLegendOff: 'Neradno',
    calLegendNone: 'Nema unosa',
    stabilityTitle: 'Stabilnost kolone',
    stabilityHint: 'Ljudi koji su radili u koloni po mjesecima; +došli, −otišli u odnosu na prethodni mjesec.',
    monthTooltip: '{members} ljudi · +{inn} / −{out} · {days} radnik-dana',
    tenureTitle: 'U ovoj koloni od',
    tenureSince: 'od {date}',
    tenureDays: { one: '{count} dan', few: '{count} dana', other: '{count} dana' },
    disciplineTitle: 'Evidencija prijava',
    disciplineHint: 'Posljednjih 90 dana. AU ne čuva stvarna vremena prijave, pa je riječ o samom unosu: istog dana, zatvoren, i ko ga je unio.',
    discSameDay: 'Uneseno istog dana',
    discMorning: 'Uneseno prije 10:00',
    discLater: 'Uneseno danima kasnije',
    discUnclosed: 'Nezatvorene smjene',
    discUnclosedHint: 'Prijavljen, nikad odjavljen: sati za taj dan nisu poznati.',
    discLeaderMissing: 'Dani bez vođe',
    discEntries: 'Unosi',
    recordedByTitle: 'Unio',
    tabBonus: 'Bonus',
    loadingBonus: 'Učitavanje bonusa',
    bonusIntro: 'Pregled po pravilima sa stranice Bonus (norma {norm} po satu) — ista formula, za ovu kolonu. Ništa se ovdje ne isplaćuje niti čuva.',
    bonusOpenPage: 'Otvori stranicu Bonus →',
    bonusMonth: 'Mjesec',
    bonusHours: 'Radni sati',
    bonusEarnings: 'Zarada od proizvodnje',
    bonusPerHour: '€ po satu',
    bonusNorm: 'Ispunjena norma',
    bonusExtras: 'Obrok i bonusi',
    bonusExtrasHint: 'Obrok {meal} · vođa {leader} · {newWorkers} novih radnika {newBonus}',
    bonusTotal: 'Ukupno',
    bonusVsMonthBefore: 'u odnosu na prethodni mjesec',
    bonusNormBar: 'Ispunjena norma ({norm} po satu = 100%)',
    bonusChartTitle: 'Ukupno po mjesecima',
    bonusChartSubtitle: 'Posljednjih 6 mjeseci; tekući mjesec još traje.',
    bonusAssumptions: 'Zarada = Σ količina pozicije × stopa bonusa, za dane kolone sa satima. Nov radnik = zaposlen unutar {window} dana prije kraja mjeseca; iznos za nove radnike računa se jednom po novom radniku mjesečno.',
  },
  sr: {
    title: 'Kolone',
    subtitle: 'Kolone, vođe i članovi prema AU.',
    readOnlyNote: 'Izmene ostaju u ovoj aplikaciji. AU se ne menja.',
    editCrew: 'Uredi kolonu',
    editNote: 'Ove ispravke ostaju u ovoj aplikaciji. AU se ne menja.',
    colHeadcount: 'U / sastav',
    colCode: 'Šifra',
    identity: 'Identitet',
    attendance: 'Prisustvo',
    couldNotSave: 'Nije sačuvano.',
    saving: 'Čuvanje…',
    searchPlaceholder: 'kolona, šifra, vođa, Bauleiter, firma…',
    searchLabel: 'Pretraga kolona',
    activeOnly: 'Samo aktivne',
    window: 'Period',
    lastDays: 'Poslednjih {days} dana',
    statActive: 'Aktivne kolone',
    statPeople: 'Prijavljenih osoba',
    statAttention: 'Za proveru',
    colCrew: 'Kolona',
    colCompany: 'Firma',
    colLeader: 'Vođa kolone',
    colBauleiter: 'Bauleiter',
    colMembers: 'Članovi',
    colRoster: 'Standardni sastav',
    colLastCheckIn: 'Poslednja prijava',
    colChecks: 'Upozorenja',
    active: 'Aktivna',
    inactive: 'Neaktivna',
    warnLeaderNotCheckedIn: 'Vođa nije prijavljen',
    warnNotOnRoster: { one: '{count} nije u sastavu', few: '{count} nisu u sastavu', other: '{count} nije u sastavu' },
    warnRosterLeft: { one: '{count} iz sastava više ne radi', few: '{count} iz sastava više ne rade', other: '{count} iz sastava više ne radi' },
    warnBauleiterOutdated: 'AU zapis kolone još pokazuje prethodnog Bauleitera',
    shownOf: '{shown} od {total} kolona',
    noCrews: 'Nema kolona koje odgovaraju.',
    loadingCrews: 'Učitavanje kolona',
    loadingCrew: 'Učitavanje kolone',
    notFound: 'Kolona nije pronađena.',
    back: 'Sve kolone',
    sectionMembers: 'Članovi — poslednjih {days} dana',
    membersHint: 'Svi koji su u AU prijavljeni sa ovom kolonom — kolona kakva stvarno radi. Dani broje samo radne dane, ne odsustva.',
    noMembers: 'Niko nije prijavljen u poslednjih {days} dana.',
    colName: 'Ime',
    colDays: 'Dana',
    colLastDay: 'Poslednji dan',
    colHired: 'Zaposlen od',
    badgeLeader: 'Vođa',
    badgeNew: 'Nov',
    badgeNotOnRoster: 'Nije u sastavu',
    badgeLeft: 'Više ne radi (HR)',
    sectionRoster: 'Standardni sastav u AU',
    rosterHint: 'Spisak u AU. Vodi se ručno i često je zastario — uporedite ga sa stvarnim prijavama.',
    noRoster: 'Nema standardnog sastava u AU.',
    colAdded: 'U sastavu od',
    colStatus: 'Status',
    employed: 'Zaposlen',
    leftOn: 'Ne radi od {date}',
    left: 'Ne radi',
    checkedIn: 'Prijavljen',
    notCheckedIn: 'Nije prijavljen',
    sectionLeaderHistory: 'Istorija vođa',
    sectionBauleiterChanges: 'Promene Bauleitera',
    current: 'trenutno',
    previously: 'pre: {name}',
    noHistory: 'Nema zabilježene istorije.',
    lastCheckIn: 'Poslednja prijava {date}',
    neverCheckedIn: 'Bez prijava',
    sectionProjects: { one: 'Projekti — {count} projekat', few: 'Projekti — {count} projekta', other: 'Projekti — {count} projekata' },
    projectsHint: 'Svi projekti na kojima ova kolona ima Aufmaß unose, najnoviji prvi. Otvorite projekat da vidite njegove trase na RTK mapi.',
    noProjects: 'Nema Aufmaß unosa za ovu kolonu.',
    colProject: 'Projekat',
    colLeaders: 'Vođa na terenu',
    colCrewDays: 'Dana kolone',
    colMetres: 'Metri',
    colPeriod: 'Rađeno',
    openOnMap: 'Otvori na mapi',
    projectDetails: 'Detalji projekta',
    rtkCount: 'RTK · {count}',
    reportedOnly: 'Samo prijavljeno',
    reportedOnlyHint: 'Postoje samo prijavljene Aufmaß količine — RTK merenja nisu snimljena, pa na mapi nema šta da se prikaže.',
    rtkByOthers: 'RTK snimile druge kolone',
    tabOverview: 'Pregled',
    tabPerformance: 'Učinak',
    loadingPerformance: 'Učitavanje učinka',
    perfWindow: 'Poslednjih {days} dana · samo dani s izveštajem (predat Aufmaß izveštaj, bez oznake greške)',
    statMetresPerDay: 'Metara po danu s izveštajem',
    vsCompany: 'u odnosu na firmu',
    statMetres: 'Metri',
    statReportDays: 'Dani s izveštajem',
    statMetresPerHour: 'Metara po radnom satu',
    trendTitle: 'Metara po danu s izveštajem, po nedeljama',
    trendSubtitle: 'Poslednjih 26 nedelja, u odnosu na prosek firme za istu nedelju.',
    trendThisCrew: 'Ova kolona',
    trendCompany: 'Prosek firme',
    qualityTitle: 'Kvalitet',
    qualityRtkFix: 'RTK fiksno rešenje',
    qualityOfSegments: '{fix} od {total} merenja (ukupno)',
    qualityAccuracy: 'Prosečna tačnost, fiksno',
    qualityNoRtk: 'Ova kolona nema snimljenih RTK merenja — njen rad postoji samo kao prijavljene količine.',
    qualityRepairs: 'Dani popravki',
    qualityFlagged: 'Označeni dani',
    qualityFlaggedHint: 'Nemoguća količina ili smena koja nije zatvorena — potrebna provera.',
    qualityNoReport: 'Dani bez izveštaja',
    qualityNoReportHint: 'Prijavljeni, ali Aufmaß izveštaj nije predat.',
    rankTitle: 'Rang',
    rankHint: 'Među {count} kolona s najmanje {min} dana s izveštajem u poslednjih {days} dana. 1 = najbolja.',
    rankMetresPerDay: 'Metara po danu',
    rankMetres: 'Ukupno metara',
    rankDays: 'Radnih dana',
    rankFix: 'Udio RTK fiksa',
    rankOf: 'od {count}',
    notRanked: 'Nije rangirana',
    buildTitle: 'Šta grade',
    buildSubtitle: 'Količina po Aufmaß poziciji, poslednjih {days} dana',
    noReportsInWindow: 'Nema predatih Aufmaß izveštaja u poslednjih {days} dana.',
    tabMap: 'Mapa i fotografije',
    loadingMap: 'Učitavanje mape',
    mapTitle: 'Gde su radili (RTK)',
    mapSummary: { one: '{n} merenja · {metres} m · {count} projekat', few: '{n} merenja · {metres} m · {count} projekta', other: '{n} merenja · {metres} m · {count} projekata' },
    mapNoRtk: 'Ova kolona nema snimljenih RTK merenja — njen rad postoji samo kao prijavljene Aufmaß količine, pa nema šta da se nacrta. Fotografije ispod su iz njenih izveštaja.',
    mapReplay: 'Reprodukuj dan po dan',
    mapPause: 'Pauza',
    mapTimeline: 'Dan',
    photosTitle: 'Najnovije fotografije',
    photosHint: 'Iz Aufmaß izveštaja i RTK merenja kolone, najnovije prve.',
    noPhotos: 'Još nema fotografija.',
    photoReport: 'Izveštaj',
    closePhoto: 'Zatvori fotografiju',
    tabAttendance: 'Prisustvo',
    loadingAttendance: 'Učitavanje prisustva',
    calendarTitle: 'Radni dani — poslednjih 26 nedelja',
    calendarSummary: '{worked} radnih dana · {off} dana upisano kao neradno · {gaps} radnih dana bez unosa',
    calHover: 'Pređite preko dana za detalje.',
    calWorked: { one: '{count} osoba radila · prijavljeno {metres} m', few: '{count} osobe radile · prijavljeno {metres} m', other: '{count} osoba radilo · prijavljeno {metres} m' },
    calNotWorking: { one: '{count} osoba upisana kao neradna', few: '{count} osobe upisane kao neradne', other: '{count} osoba upisano kao neradno' },
    calNothing: 'Nema unosa',
    calLegendWorked: 'Radili (tamnije = više metara)',
    calLegendOff: 'Neradno',
    calLegendNone: 'Nema unosa',
    stabilityTitle: 'Stabilnost kolone',
    stabilityHint: 'Ljudi koji su radili u koloni po mesecima; +došli, −otišli u odnosu na prethodni mesec.',
    monthTooltip: '{members} ljudi · +{inn} / −{out} · {days} radnik-dana',
    tenureTitle: 'U ovoj koloni od',
    tenureSince: 'od {date}',
    tenureDays: { one: '{count} dan', few: '{count} dana', other: '{count} dana' },
    disciplineTitle: 'Evidencija prijava',
    disciplineHint: 'Poslednjih 90 dana. AU ne čuva stvarna vremena prijave, pa je reč o samom unosu: istog dana, zatvoren, i ko ga je uneo.',
    discSameDay: 'Uneseno istog dana',
    discMorning: 'Uneseno pre 10:00',
    discLater: 'Uneseno danima kasnije',
    discUnclosed: 'Nezatvorene smene',
    discUnclosedHint: 'Prijavljen, nikad odjavljen: sati za taj dan nisu poznati.',
    discLeaderMissing: 'Dani bez vođe',
    discEntries: 'Unosi',
    recordedByTitle: 'Uneo',
    tabBonus: 'Bonus',
    loadingBonus: 'Učitavanje bonusa',
    bonusIntro: 'Pregled po pravilima sa stranice Bonus (norma {norm} po satu) — ista formula, za ovu kolonu. Ništa se ovde ne isplaćuje niti čuva.',
    bonusOpenPage: 'Otvori stranicu Bonus →',
    bonusMonth: 'Mesec',
    bonusHours: 'Radni sati',
    bonusEarnings: 'Zarada od proizvodnje',
    bonusPerHour: '€ po satu',
    bonusNorm: 'Ispunjena norma',
    bonusExtras: 'Obrok i bonusi',
    bonusExtrasHint: 'Obrok {meal} · vođa {leader} · {newWorkers} novih radnika {newBonus}',
    bonusTotal: 'Ukupno',
    bonusVsMonthBefore: 'u odnosu na prethodni mesec',
    bonusNormBar: 'Ispunjena norma ({norm} po satu = 100%)',
    bonusChartTitle: 'Ukupno po mesecima',
    bonusChartSubtitle: 'Poslednjih 6 meseci; tekući mesec još traje.',
    bonusAssumptions: 'Zarada = Σ količina pozicije × stopa bonusa, za dane kolone sa satima. Nov radnik = zaposlen unutar {window} dana pre kraja meseca; iznos za nove radnike računa se jednom po novom radniku mjesečno.',
  },
})
